import { infiniteQueryOptions, queryOptions, skipToken } from '@tanstack/react-query';

import {
    RecurringSubscriptionBillingAmountCalculationQuery,
    RecurringSubscriptionBillingAmountListQuery,
    RecurringSubscriptionData,
    RecurringSubscriptionListQuery,
} from 'chaika-frontend-types/frontend-cms';
import { Currency } from 'chaika-enums';

import { RecurringSubscriptionApi } from '@api/RecurringSubscriptionApi';
import { patientService } from '@modules/patient/services/patient/patientService';
import { userService } from '@user/services/userService';
import { subscriptionService } from '@patient/services/subscriptionService';
import { paymentsService } from '@modules/payments/services/paymentsService';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';

type EhrSubscriptionsQuery = Omit<RecurringSubscriptionListQuery, 'patientId'>;
type DoctorSubscriptionsQuery = Omit<RecurringSubscriptionListQuery, 'doctorId'>;

const LIST_PAGE_LIMIT = 50;

export const recurringSubscriptionKeys = {
    all: ['recurringSubscriptions'] as const,
    lists: () => [...recurringSubscriptionKeys.all, 'list'] as const,
    list: (filters: RecurringSubscriptionListQuery) => [...recurringSubscriptionKeys.lists(), { filters }] as const,
    paginatedList: (filters: RecurringSubscriptionListQuery) =>
        [...recurringSubscriptionKeys.all, 'paginatedList', { filters }] as const,
    detail: (id: string) => [...recurringSubscriptionKeys.all, 'detail', id] as const,
    billingAmounts: (params: RecurringSubscriptionBillingAmountListQuery) =>
        [...recurringSubscriptionKeys.all, 'billingAmounts', params] as const,
    calculatedBillingAmount: (params: Partial<RecurringSubscriptionBillingAmountCalculationQuery>) =>
        [...recurringSubscriptionKeys.all, 'calculatedBillingAmount', params] as const,
    byEhr: (ehrId: string, params: EhrSubscriptionsQuery) =>
        [...recurringSubscriptionKeys.all, 'byEhr', ehrId, params] as const,
    byDoctor: (doctorId: string, params: DoctorSubscriptionsQuery) =>
        [...recurringSubscriptionKeys.all, 'byDoctor', doctorId, params] as const,
};

export const recurringSubscriptionQueryOptions = {
    list: (filters: RecurringSubscriptionListQuery = {}) =>
        queryOptions({
            queryKey: recurringSubscriptionKeys.list(filters),
            queryFn: async () => {
                const { recurringSubscriptions } = await RecurringSubscriptionApi.find(filters);
                await loadAdditionalData(recurringSubscriptions);

                return recurringSubscriptions;
            },
        }),
    paginatedList: (filters: RecurringSubscriptionListQuery = {}) =>
        infiniteQueryOptions({
            queryKey: recurringSubscriptionKeys.paginatedList(filters),
            queryFn: async ({ pageParam }) => {
                const { recurringSubscriptions, count } = await RecurringSubscriptionApi.find({
                    ...filters,
                    limit: pageParam.limit,
                    offset: pageParam.offset,
                });
                await loadAdditionalData(recurringSubscriptions);

                return { recurringSubscriptions, count };
            },
            initialPageParam: { limit: LIST_PAGE_LIMIT, offset: 0 },
            getNextPageParam: (lastPage, _, lastPageParam) => {
                const nextOffset = lastPageParam.offset + lastPageParam.limit;

                return nextOffset < lastPage.count ? { limit: lastPageParam.limit, offset: nextOffset } : null;
            },
        }),
    byId: (id: string) =>
        queryOptions({
            queryKey: recurringSubscriptionKeys.detail(id),
            queryFn: async () => {
                const recurringSubscription = await RecurringSubscriptionApi.get(id);
                await loadAdditionalData([recurringSubscription]);

                return recurringSubscription;
            },
        }),
    billingAmounts: (params: RecurringSubscriptionBillingAmountListQuery) =>
        queryOptions({
            queryKey: recurringSubscriptionKeys.billingAmounts(params),
            queryFn: async () => {
                const { billingAmounts } = await RecurringSubscriptionApi.getBillingAmount(params);

                return billingAmounts;
            },
        }),
    calculatedBillingAmount: ({ doctorId, ...params }: Partial<RecurringSubscriptionBillingAmountCalculationQuery>) =>
        queryOptions({
            queryKey: recurringSubscriptionKeys.calculatedBillingAmount({ doctorId, ...params }),
            queryFn: doctorId
                ? async () => {
                      try {
                          return await RecurringSubscriptionApi.calculateBillingAmount({ doctorId, ...params });
                      } catch {
                          return { amount: 0, currency: Currency.RUB, hasCustomBillingAmount: false };
                      }
                  }
                : skipToken,
        }),
    byEhr: (ehrId: string, params: EhrSubscriptionsQuery = {}) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: recurringSubscriptionKeys.byEhr(ehrId, params),
            queryFn: async () => {
                const { recurringSubscriptions } = await RecurringSubscriptionApi.find({
                    patientId: ehrId,
                    ...params,
                });
                await loadAdditionalData(recurringSubscriptions);

                return recurringSubscriptions;
            },
        }),
    byDoctor: (doctorId: string, params: DoctorSubscriptionsQuery = {}) =>
        queryOptions({
            queryKey: recurringSubscriptionKeys.byDoctor(doctorId, params),
            queryFn: () =>
                RecurringSubscriptionApi.find({ doctorId, ...params }).then(
                    ({ recurringSubscriptions }) => recurringSubscriptions,
                ),
        }),
};

async function loadAdditionalData(recurringSubscriptions: RecurringSubscriptionData[]): Promise<void> {
    const { patientIds, doctorIds, subscriptionIds, paymentIds } = recurringSubscriptions.reduce(
        (acc, cur) => {
            if (cur.patientId) {
                acc.patientIds.add(cur.patientId);
            }
            if (cur.buyerId) {
                acc.patientIds.add(cur.buyerId);
            }
            if (cur.doctorId) {
                acc.doctorIds.add(cur.doctorId);
            }
            if (cur.subscriptionId) {
                acc.subscriptionIds.add(cur.subscriptionId);
            }
            cur.paymentIds.forEach(id => acc.paymentIds.add(id));
            return acc;
        },
        {
            patientIds: new Set<string>(),
            doctorIds: new Set<string>(),
            subscriptionIds: new Set<string>(),
            paymentIds: new Set<string>(),
        },
    );

    await Promise.all([
        patientService.loadByIds(toArrayFromIterable(patientIds)),
        userService.loadIfNotExist(toArrayFromIterable(doctorIds)),
        subscriptionService.getListByIds(toArrayFromIterable(subscriptionIds)),
        paymentsService.loadByIds(toArrayFromIterable(paymentIds)),
    ]);
}
